import { parseCurrency } from '@utils/currency.util';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload';

import { offerApi } from '../apis/offerApis';
import { OfferFormData, OfferPayload } from '../interfaces/offersInterface';
import { coerceToDate } from './normalizeDate';

export type UploadedFields = {
  success: { field: string; value: string }[];
  error: string[];
};

export async function createPayloadFromFormData(
  productId: string,
  formData: OfferFormData
): Promise<{ offerPayload: OfferPayload; uploadedFields: UploadedFields }> {
  const tagValue = formData.sensitiveData.isMailPromotional
    ? 'promotional'
    : undefined;
  const uploadedFields: UploadedFields = {
    success: [],
    error: [],
  };

  const handleUploadImgToS3 = async (
    formField: string,
    img: UploadChangeParam<UploadFile>
  ) => {
    const url = await offerApi.uploadOfferImg(productId, img.file as RcFile);
    if (url) {
      uploadedFields.success.push({ field: formField, value: url });
    } else {
      uploadedFields.error.push(formField);
    }
    return url;
  };

  // Descontos (multi valores)
  const discounts = formData.sensitiveData.hasRetryDiscounts
    ? formData.sensitiveData.retryDiscounts.map(
        ({ active, percentage, isDirectFallback }) => ({
          active,
          percentage: Number(percentage),
          isDirectFallback,
        })
      )
    : [];

  // Counter flags
  const hasOfferCounter =
    formData.counter.typeCounter === 'offerCounter' && formData.hasCounter;

  const hasUniqueVisitCounter =
    formData.counter.typeCounter === 'uniqueVisitCounter' &&
    formData.hasCounter;

  const hasMultiCounter =
    formData.counter.typeCounter === 'multiCounter' && formData.hasCounter;

  let accessProfileId = formData.sensitiveData.plan
    ? Number(formData.sensitiveData.plan)
    : undefined;
  if (!accessProfileId && formData.accessProfileId) {
    accessProfileId = Number(formData.accessProfileId);
  }
  const offerValue = parseCurrency(formData.sensitiveData.fullPrice);
  if (!offerValue) {
    throw new Error('Valor da oferta deve ser um número válido');
  }

  const offerPayload: OfferPayload = {
    productSellGroupId: Number(productId),
    offerName: formData.offerName,
    offerDescription: formData.offerDescription,
    offerValue,
    offerInstallments: Number(formData.sensitiveData.installments),
    startDate: formData.sensitiveData.startDate,
    endDate: formData.sensitiveData.endDate,
    tag: tagValue,
    renewalOfferId: formData.sensitiveData.renewalOfferId
      ? Number(formData.sensitiveData.renewalOfferId)
      : undefined,
    replaceDefaultOffer: formData.sensitiveData.offerStandard,
    renewalDisabled: formData.sensitiveData.renewalDisabled,
    acquisitionId: Number(formData.sensitiveData.acquisition),
    seasonalityId: Number(formData.sensitiveData.seasonality),
    typeId: Number(formData.sensitiveData.type),
    onlyFull: formData.sensitiveData.onlyFull,
    profileId: formData.sensitiveData.profileId
      ? Number(formData.sensitiveData.profileId)
      : undefined,
    warrantyDays: formData.sensitiveData.warrantyDays
      ? Number(formData.sensitiveData.warrantyDays)
      : undefined,
    flowId: formData.sensitiveData.flowId
      ? Number(formData.sensitiveData.flowId)
      : null,
    discounts,
    promotionalAmount:
      formData.hasPromotionalOffer &&
      formData.promotionalOffer.promotionalAmount
        ? parseCurrency(formData.promotionalOffer.promotionalAmount)
        : undefined,
    qtdPromotionalDays:
      formData.hasPromotionalOffer &&
      formData.promotionalOffer.qtdPromotionalDays
        ? Number(formData.promotionalOffer.qtdPromotionalDays)
        : undefined,
    accessProfileId: accessProfileId ? Number(accessProfileId) : undefined,

    // Métodos de pagamento
    isAvailableCreditCard:
      formData.sensitiveData.paymentMethods.includes('creditCard'),
    isAvailableNuPay: formData.sensitiveData.paymentMethods.includes('nupay'),
    isAvailablePagaleve:
      formData.sensitiveData.paymentMethods.includes('pagaleve'),
    isAvailablePix: formData.sensitiveData.paymentMethods.includes('pix'),
    isAvailableBillet: formData.sensitiveData.paymentMethods.includes('billet'),

    disclaimer: formData.sensitiveData.disclaimer || undefined,
    displayPrice: parseCurrency(formData.sensitiveData.displayPrice),
    isDisplayCoupon: formData.sensitiveData.isDisplayCoupon,

    checkoutConfig: {
      // Dados adicionais do checkout
      purchaseBtnLabel: formData.sensitiveData.purchaseBtnLabel || undefined,
      termsRef: formData.sensitiveData.termsRef,
      privacyPolicyRef: formData.sensitiveData.privacyPolicyRef,

      // Analytics
      facebookPixels: formData.analytics.facebookPixels || undefined,
      googleAnalyticsIds: formData.analytics.googleAnalyticsIds || undefined,

      bannerWeb:
        formData.bannerWeb instanceof Object
          ? await handleUploadImgToS3('bannerWeb', formData.bannerWeb)
          : formData.bannerWeb,
      bannerMobile:
        formData.bannerMobile instanceof Object
          ? await handleUploadImgToS3('bannerMobile', formData.bannerMobile)
          : formData.bannerMobile,
      bannerSideWeb:
        formData.bannerSideWeb instanceof Object
          ? await handleUploadImgToS3('bannerSideWeb', formData.bannerSideWeb)
          : formData.bannerSideWeb,
      logo:
        formData.logo instanceof Object
          ? await handleUploadImgToS3('logo', formData.logo)
          : formData.logo,

      // Contadores
      enableCounter: hasOfferCounter,
      ...(hasOfferCounter && {
        counter: {
          colorBackground: formData.counter.offerCounter.bgCounter,
          bannerRightWeb:
            formData.counter.offerCounter.rightImg instanceof Object
              ? await handleUploadImgToS3(
                  'counter.offerCounter.rightImg',
                  formData.counter.offerCounter.rightImg
                )
              : formData.counter.offerCounter.rightImg,
          bannerRightMobile:
            formData.counter.offerCounter.rightImgMobile instanceof Object
              ? await handleUploadImgToS3(
                  'counter.offerCounter.rightImgMobile',
                  formData.counter.offerCounter.rightImgMobile
                )
              : formData.counter.offerCounter.rightImgMobile,
          bannerLeft:
            formData.counter.offerCounter.leftImg instanceof Object
              ? await handleUploadImgToS3(
                  'counter.offerCounter.leftImg',
                  formData.counter.offerCounter.leftImg
                )
              : formData.counter.offerCounter.leftImg,
          bannerLeftMobile:
            formData.counter.offerCounter.leftImgMobile instanceof Object
              ? await handleUploadImgToS3(
                  'counter.offerCounter.leftImgMobile',
                  formData.counter.offerCounter.leftImgMobile
                )
              : formData.counter.offerCounter.leftImgMobile,
        },
      }),

      enableUniqueVisitCounter: hasUniqueVisitCounter,
      ...(hasUniqueVisitCounter && {
        uniqueVisitCounter: {
          timeoutRedirectUrl:
            formData.counter.uniqueVisitCounter.timeoutRedirectUrl,
          timerMinute: Number(formData.counter.uniqueVisitCounter.timerMinute),
          timerSecond: Number(formData.counter.uniqueVisitCounter.timerSecond),
          countdownPhrase: formData.counter.uniqueVisitCounter.countdownPhrase,
          cacheExpirationTime: Number(
            formData.counter.uniqueVisitCounter.cacheExpirationTime
          ),
        },
      }),

      enableMultiCounter: hasMultiCounter,
      ...(hasMultiCounter && {
        multiCounter: {
          colorBackground: formData.counter.multiCounter.bgCounter,
          bannerRightWeb:
            formData.counter.multiCounter.rightImg instanceof Object
              ? await handleUploadImgToS3(
                  'counter.multiCounter.rightImg',
                  formData.counter.multiCounter.rightImg
                )
              : formData.counter.multiCounter.rightImg,
          bannerRightMobile:
            formData.counter.multiCounter.rightImgMobile instanceof Object
              ? await handleUploadImgToS3(
                  'counter.multiCounter.rightImgMobile',
                  formData.counter.multiCounter.rightImgMobile
                )
              : formData.counter.multiCounter.rightImgMobile,
          bannerLeft:
            formData.counter.multiCounter.leftImg instanceof Object
              ? await handleUploadImgToS3(
                  'counter.multiCounter.leftImg',
                  formData.counter.multiCounter.leftImg
                )
              : formData.counter.multiCounter.leftImg,
          bannerLeftMobile:
            formData.counter.multiCounter.leftImgMobile instanceof Object
              ? await handleUploadImgToS3(
                  'counter.multiCounter.leftImgMobile',
                  formData.counter.multiCounter.leftImgMobile
                )
              : formData.counter.multiCounter.leftImgMobile,
          schedules: JSON.stringify(
            formData.counter.multiCounter.schedules.map(schedule => ({
              startTime: coerceToDate(schedule.startTime)?.toISOString(),
              endTime: coerceToDate(schedule.endTime)?.toISOString(),
            }))
          ),
        },
      }),
    },

    // Produto físico
    ...(formData.isPhysicProduct && {
      physicProduct: {
        primaryColor: formData.physicProduct.primaryColor,
        footerText: formData.physicProduct.footerText,
        isFreeShipping: formData.physicProduct.isFreeShipping,
        title: formData.physicProduct.seo.title,
        siteName: formData.physicProduct.seo.siteName,
        keywords: `[${formData.physicProduct.seo.keywords
          .map(keyWord => `"${keyWord}"`)
          .join(', ')}]`,
        smallDescription: formData.physicProduct.seo.smallDescription,
        largeDescription: formData.physicProduct.seo.largeDescription,
        image:
          formData.physicProduct.seo.image instanceof Object
            ? await handleUploadImgToS3(
                'physicProduct.seo.image',
                formData.physicProduct.seo.image
              )
            : formData.physicProduct.seo.image,
        favicon:
          formData.physicProduct.seo.favicon instanceof Object
            ? await handleUploadImgToS3(
                'physicProduct.seo.favicon',
                formData.physicProduct.seo.favicon
              )
            : formData.physicProduct.seo.favicon,
      },
    }),

    // Mobile stores
    isAvailableMobile: formData.isAvailableMobile,
    itunesStoreId: formData.mobile.itunesStoreId || undefined,
    googleStoreId: formData.mobile.googleStoreId || undefined,
  };

  return { offerPayload, uploadedFields };
}
